import Component from '@glimmer/component'
import { action } from '@ember/object'
import { service } from '@ember/service'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import { tracked } from '@glimmer/tracking'

import { Changeset } from 'ember-changeset'
import PasswordValidator from 'teacher-ui-client/components/form-wrapper/edit-password-form/validator'
import type Student from 'teacher-ui-client/models/student'
import type { Task, TaskInstance } from 'ember-concurrency'
import type { IntlService } from 'ember-intl'
import type { BufferedChangeset } from 'ember-changeset/types'
import { FormField } from 'teacher-ui-client/components/form-field'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { t } from 'ember-intl'
import { optional } from 'teacher-ui-client/helpers/optional'
import { on } from '@ember/modifier'
import { FormWrapperSubmitButton } from 'teacher-ui-client/components/form-wrapper/submit-button'
import { or } from 'ember-truth-helpers'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import submitButtonState from 'teacher-ui-client/helpers/submit-button-state'
import type Owner from '@ember/owner'

type MultiplePasswordsTask = Task<void, [Student[], string]>

interface Args {
  students: Student[]
  setMultiplePasswordsTask: MultiplePasswordsTask
  onCancel: () => void
}

export class FormWrapperEditPasswordForm extends Component<Args> {
  @tracked taskInstance?: TaskInstance<void>

  @service intl!: IntlService

  @tracked changeset: BufferedChangeset

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    const data = { plainPassword: '' }
    const { intl } = this
    const changeset = Changeset(data, lookupValidator(PasswordValidator, { intl }), PasswordValidator)
    this.changeset = changeset
  }

  @action
  onSubmit(event: SubmitEvent) {
    event.preventDefault()
    const password = this.changeset.get('plainPassword').trim()
    const studentsToUpdate = this.args.students
    const setMultiplePasswordsTaskInstance = this.args.setMultiplePasswordsTask.perform(studentsToUpdate, password)
    this.taskInstance = setMultiplePasswordsTaskInstance
    return false
  }

  <template>
    <form {{on "submit" this.onSubmit}} autocomplete="off">
      <div class="p-4">
        <p class="mb-3">
          {{t "manage.manageClasses.students.bulkActions.totalSelectedStudents" count=@students.length htmlSafe=true}}
        </p>
        <FormField
          class="max-w-50"
          @changeset={{this.changeset}}
          @propertyName="plainPassword"
          @labelText={{t "manage.manageClasses.students.bulkActions.editPassword.password"}}
          as |field|
        >
          <field.label class="font-bold" />
          <field.input />
        </FormField>
      </div>
      <div class="border-dusty-black-50 border-t p-2 text-right">
        <UiButton data-test-cancel type="reset" {{on "click" (optional @onCancel)}}>
          {{t "manage.manageClasses.students.bulkActions.cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-submit
          @disabled={{or this.changeset.isPristine this.changeset.isInvalid}}
          @defaultText={{t "manage.manageClasses.students.bulkActions.update"}}
          @appearance={{ButtonAppearance.regular}}
          @state={{submitButtonState this.taskInstance null}}
        />
      </div>
    </form>
  </template>
}

export default FormWrapperEditPasswordForm
